import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import styled from 'react-emotion';

import { Divider } from 'web-ui-components/lib/atoms/layout';
import { AccountAuthorisationPanel, AccountPaymentSubmittedTable } from 'web-ui-components/lib/finance/accounts';
import { BackButton } from 'web-ui-components/lib/molecules/navigation';
import { NavigationButton } from 'web-ui-components/lib/navigation/buttons';
import { PageHeading, SectionHeading } from 'web-ui-components/lib/typography/headings';

import PageNavigation from '../../../sharedComponents/PageNavigation/PageNavigation';
import ChapsDeadlineMissed from './ChapsDeadlineMissed';
import withContent from '../../../containers/withContent';
import { withScrollTop } from '../../../containers/withScrollTop';
import chapsReviewContent, { contentPropTypes } from './chapsReview.content';
import { paymentReviewContent } from '../../../sharedComponents/PaymentReview/PaymentReview.content';
import { hasApiCallInProgress } from '../../../states/api/selectors';
import Notifications from '../../../sharedComponents/Notifications';
import {
	getChapsPaymentsForReview,
	getChapsPaymentsRequireAuthorisers,
	getChapsDeadlineMissed,
} from '../../../states/chaps/selectors';
import { goBackPage, goToPage, pages } from '../../../states/page/actions';
import ChapsAuthoriserViewer from './ChapsAuthoriserViewer';
import { ErrorValidationMessage } from '../../../sharedComponents/ErrorValidationMessage/ErrorValidationMessage';
import ChapsAuthoriserSelector from './ChapsAuthoriserSelector';
import {
	isBatch,
	getBatchName,
	getBatchAuthorisers,
	getBatchGroupAuthorisers,
	getBatchRequiresAuthorisation,
	getBatchPaymentRequiresAuthorisers,
	getBatchIsDefaultAuthorisers,
} from '../../../states/batch/selectors';
import { getAuthorisationMessage } from '../../../helpers/fieldHelpers';
import BatchAuthoriserViewer from '../../../fasterPayments/components/Review/BatchAuthoriserViewer';
import { BatchAuthoriserSelector } from '../../../fasterPayments/components/Review/BatchAuthoriserSelector';
import { EnhancedBatchProgressTakeover } from '../../../sharedComponents/BatchProgressTakeover/BatchProgressTakeover';
import { isManagePaymentEdit } from '../../../states/modes/selectors';
import { setSelectedPaymentId } from '../../../states/payeeSelector/actions';
import { getUserId } from '../../../states/user/selectors';

const mapState = (state) => ({
	hasApiCallInProgress: hasApiCallInProgress(state),
	paymentsRequireAuthorisers: getChapsPaymentsRequireAuthorisers(state),
	paymentsToReview: getChapsPaymentsForReview(state),
	deadlineMissed: getChapsDeadlineMissed(state),
	isBatch: isBatch(state),
	batchName: getBatchName(state),
	authorisers: getBatchAuthorisers(state),
	groupAuthorisers: getBatchGroupAuthorisers(state),
	batchRequiresAuthorisers: getBatchPaymentRequiresAuthorisers(state),
	batchRequiresAuthorisation: getBatchRequiresAuthorisation(state),
	isBatchDefaultAuthorisers: getBatchIsDefaultAuthorisers(state),
	isManagePaymentEdit: isManagePaymentEdit(state),
	userId: getUserId(state),
});

const mapDispatch = (dispatch) => ({
	handleBack: () => dispatch(goBackPage()),
	navigateToEffectiveWarningPage: (page) => dispatch(goToPage(page)),
	setSelectedPaymentId: () => dispatch(setSelectedPaymentId()),
});

const StyledAuthorisationWrapper = styled('div')`
	margin: 0 auto;
	max-width: 550px;
	text-align: center;

	span {
		font-size: 20px;
	}
`;

class ChapsReviewPage extends Component {
	constructor(props) {
		super(props);
		this.state = { showAuthModal: false, paymentSelectedId: undefined, showBatchAuthorisers: false };
		this.handleBackClick = this.handleBackClick.bind(this);
		this.handleShowBatchAuthorisers = this.handleShowBatchAuthorisers.bind(this);
		this.handleHideBatchAuthorisers = this.handleHideBatchAuthorisers.bind(this);
		this.handleContinue = this.handleContinue.bind(this);
	}
	componentDidMount() {
		const { handlePaymentInReview } = this.props;
		handlePaymentInReview(true);
	}

	componentWillUnmount() {
		this.props.handlePaymentInReview(false);
	}

	handleBackClick() {
		const { setSelectedPaymentId, handleBack } = this.props;
		setSelectedPaymentId();
		handleBack();
	}

	handleShowBatchAuthorisers() {
		this.setState({ showBatchAuthorisers: true });
	}

	handleHideBatchAuthorisers() {
		this.setState({ showBatchAuthorisers: false });
	}

	handleContinue() {
		const { navigateToEffectiveWarningPage, handleContinue, isManagePaymentEdit } = this.props;
		if (isManagePaymentEdit) return handleContinue();
		return navigateToEffectiveWarningPage(pages.EffectiveWarning.paymentReason);
	}

	render() {
		const {
			batchRequiresAuthorisers,
			handleBack,
			content,
			hasApiCallInProgress,
			paymentsRequireAuthorisers,
			paymentsToReview,
			batchName,
			isBatch,
			deadlineMissed,
			handleRoute,
			batchRequiresAuthorisation,
			authorisers,
			groupAuthorisers,
			isBatchDefaultAuthorisers,
			userId,
			hasGroupApprovalsEnabled,
		} = this.props;

		const renderDeadlineMissed = () => <ChapsDeadlineMissed content={content} handleRoute={handleRoute} />;

		const headerData = [
			{
				type: 'account',
				label: content.fromLabel,
			},
			{
				type: 'account',
				label: content.toLabel,
			},
			{
				type: 'currency',
				label: content.amtLabel,
			},
			{
				type: 'iban',
				label: content.refLabel,
			},
			{
				type: 'date',
				label: content.dateLabel,
			},
			{
				type: 'type',
				label: content.typeLabel,
			},
			{
				type: 'currency',
				label: content.feesLabel,
			},
			{
				type: 'authorisation',
				label: '',
			},
		];

		const showAuthButton = !isBatchDefaultAuthorisers;

		const hasNoAvailableAuthorisers = groupAuthorisers && groupAuthorisers.length === 0;

		const authorisersRequired = isBatch ? batchRequiresAuthorisers : paymentsRequireAuthorisers;

		const selectAuthoriserButtonLabel = authorisers.length
			? content.changeAuthoriserButton
			: content.selectAuthoriserButton;

		const { noAvailableAuthorisersErrorMessage } = paymentReviewContent;

		return (
			<div data-cy="chaps-review-page">
				<BackButton onClick={handleBack} disabled={hasApiCallInProgress} />
				<div>
					<PageHeading isFlush data-test-id="chaps-review-page">
						{content.PageHeader}
					</PageHeading>
					<p className="u-margin-top-1">{content.PageSubHeader}</p>
					<p>{content.pageSubHeaderMsg}</p>
					<div className="u-margin-bottom-2">
						<Notifications />
					</div>
				</div>
				<div>
					{isBatch && (
						<div>
							<SectionHeading>{batchName}</SectionHeading>
							<Divider />
						</div>
					)}

					{isBatch && batchRequiresAuthorisation && (
						<AccountAuthorisationPanel titleText={content.AuthorisationHeader}>
							<StyledAuthorisationWrapper>
								<p>
									{authorisers.length
										? getAuthorisationMessage(authorisers, userId, content.AuthorisationByUserSubTitle)
										: content.AuthorisationBodyMsg}
								</p>
								{authorisersRequired && hasNoAvailableAuthorisers && (
									<ErrorValidationMessage data-test-id="has-no-available-authorisers-error-validation-message">
										{noAvailableAuthorisersErrorMessage}
									</ErrorValidationMessage>
								)}
								{showAuthButton && (
									<NavigationButton.Tertiary
										data-test-id="chaps-view-authorisers-button"
										isInline
										labelText={hasGroupApprovalsEnabled ? content.viewAuthoriserButton : selectAuthoriserButtonLabel}
										onClick={this.handleShowBatchAuthorisers}
									/>
								)}
							</StyledAuthorisationWrapper>
						</AccountAuthorisationPanel>
					)}

					<AccountPaymentSubmittedTable
						headerData={headerData}
						viewAuthorisersOnClick={(id) => this.setState({ showAuthModal: true, paymentSelectedId: id })}
						changeAuthorisersOnClick={(id) => this.setState({ showAuthModal: true, paymentSelectedId: id })}
						selectAuthoriserOnClick={(id) => this.setState({ showAuthModal: true, paymentSelectedId: id })}
						tableData={paymentsToReview}
					/>
				</div>

				{hasGroupApprovalsEnabled ? (
					<div>
						<ChapsAuthoriserViewer
							isOpen={this.state.showAuthModal}
							paymentId={this.state.paymentSelectedId}
							onClose={() => this.setState({ showAuthModal: false })}
						/>
						<BatchAuthoriserViewer isOpen={this.state.showBatchAuthorisers} onClose={this.handleHideBatchAuthorisers} />
					</div>
				) : (
					<div>
						<ChapsAuthoriserSelector
							isOpen={this.state.showAuthModal}
							paymentId={this.state.paymentSelectedId}
							onClose={() => this.setState({ showAuthModal: false })}
						/>
						<BatchAuthoriserSelector
							isOpen={this.state.showBatchAuthorisers}
							onClose={this.handleHideBatchAuthorisers}
						/>
					</div>
				)}

				<EnhancedBatchProgressTakeover contentKey="reviewPayments" />
				<PageNavigation
					handleContinue={this.handleContinue}
					showContinue
					enableContinue={!hasApiCallInProgress && !authorisersRequired}
					showBack
					handleBack={this.handleBackClick}
					enableBack={!hasApiCallInProgress}
					continueButtonText={content.continueButtonText}
					showLoading={hasApiCallInProgress}
				/>
				{deadlineMissed && renderDeadlineMissed()}
			</div>
		);
	}
}

ChapsReviewPage.propTypes = {
	handleBack: PropTypes.func.isRequired,
	content: PropTypes.shape(contentPropTypes),
	hasApiCallInProgress: PropTypes.bool.isRequired,
	paymentsRequireAuthorisers: PropTypes.bool,
	paymentsToReview: PropTypes.arrayOf(PropTypes.shape({})),
	handlePaymentInReview: PropTypes.func.isRequired,
	deadlineMissed: PropTypes.bool,
	handleRoute: PropTypes.func.isRequired,
	batchRequiresAuthorisers: PropTypes.bool,
	batchRequiresAuthorisation: PropTypes.bool,
	isBatch: PropTypes.bool,
	batchName: PropTypes.string,
	authorisers: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, id: PropTypes.string })),
	isBatchDefaultAuthorisers: PropTypes.bool,
	navigateToEffectiveWarningPage: PropTypes.func,
	handleContinue: PropTypes.func.isRequired,
	isManagePaymentEdit: PropTypes.bool.isRequired,
};

export default compose(
	connect(mapState, mapDispatch),
	withContent({ content: chapsReviewContent }),
	withScrollTop
)(ChapsReviewPage);
